import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import CareLinkService from '../../../services/CareLinkService';
import { formatDateToDisplay } from '../../../utils/dateHelper';
import './CareLink.css';

const SUPPORTED_LANGUAGES = ['es', 'en', 'pt'];

const resolveBrowserLanguage = () => {
  const browserLang = (navigator.language || navigator.userLanguage || 'es')
    .slice(0, 2)
    .toLowerCase();
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : 'es';
};

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) {
    return null;
  }
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

const buildDisplayName = (profile) => {
  const parts = [
    profile.firstName,
    profile.lastName,
    profile.secondLastName,
  ].filter(Boolean);
  return parts.join(' ');
};

const splitInstructions = (text) => {
  if (!text || !String(text).trim()) {
    return [];
  }
  return String(text)
    .split(/\r?\n|[;|]/)
    .map((line) => line.replace(/^\d+[).\-\s]+/, '').trim())
    .filter(Boolean);
};

const hasText = (value) => value != null && String(value).trim() !== '';

const DetailRow = ({ label, value }) => {
  if (!hasText(value)) {
    return null;
  }
  const lines = splitInstructions(value);
  return (
    <div className="carelink-detail-row">
      <span className="carelink-detail-label">{label}</span>
      {lines.length > 1 ? (
        <ul className="carelink-detail-lines">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : (
        <span className="carelink-detail-value">{lines[0] || value}</span>
      )}
    </div>
  );
};

const CallButton = ({ phone, label }) => {
  if (!hasText(phone)) {
    return null;
  }
  return (
    <a className="carelink-call-button" href={`tel:${phone}`}>
      <i className="fa fa-phone"></i>
      {label}
    </a>
  );
};

const InstructionBlock = ({ title, text }) => {
  const steps = splitInstructions(text);
  if (steps.length === 0) {
    return null;
  }
  return (
    <section className="carelink-section-card is-muted">
      <h2 className="carelink-section-title">{title}</h2>
      <ol className="carelink-instruction-list">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </section>
  );
};

const CareLink = () => {
  const { publicToken } = useParams();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    i18n.changeLanguage(resolveBrowserLanguage());
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!publicToken) {
        setError(t('carelink.errors.missingToken'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const data = await CareLinkService.getByPublicToken(publicToken);
        if (!cancelled) {
          setProfile(data);
        }
      } catch (err) {
        if (!cancelled) {
          setProfile(null);
          setError(
            err?.status === 404
              ? t('carelink.errors.notFound')
              : (err?.message || t('carelink.errors.loadFailed'))
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [publicToken, t]);

  const age = useMemo(() => calculateAge(profile?.dateOfBirth), [profile]);
  const displayName = useMemo(() => (profile ? buildDisplayName(profile) : ''), [profile]);

  const primaryContact = useMemo(() => {
    if (!profile?.contacts?.length) {
      return null;
    }
    return (
      profile.contacts.find((contact) => contact.primaryContact && contact.enabled !== false)
      || profile.contacts.find((contact) => contact.emergencyContact && contact.enabled !== false)
      || profile.contacts.find((contact) => contact.enabled !== false)
      || profile.contacts[0]
    );
  }, [profile]);

  const otherContacts = useMemo(
    () => (profile?.contacts || []).filter((contact) => contact.enabled !== false && contact !== primaryContact),
    [profile, primaryContact]
  );

  const medications = useMemo(
    () => (profile?.medications || []),
    [profile]
  );

  if (loading) {
    return (
      <div className="carelink-profile-page">
        <div className="carelink-profile-shell">
          <div className="carelink-detect-banner">
            <div className="carelink-detect-icon">
              <i className="fa fa-wifi"></i>
            </div>
            <div>
              <p className="carelink-detect-title">{t('carelink.banner.title')}</p>
              <p className="carelink-detect-subtitle">{t('carelink.banner.loading')}</p>
            </div>
          </div>
          <div className="carelink-state-box">
            <div className="carelink-spinner"></div>
            <h2>{t('carelink.loadingTitle')}</h2>
            <p>{t('carelink.loadingText')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="carelink-profile-page">
        <div className="carelink-profile-shell">
          <div className="carelink-state-box">
            <h2>{t('carelink.errors.title')}</h2>
            <p>{error || t('carelink.errors.notFound')}</p>
          </div>
        </div>
      </div>
    );
  }

  const residenceCity = profile.careCenter?.city;
  const showPrioritySupport = Boolean(
    profile.hasDisability
    || hasText(profile.disabilityType)
    || profile.requiresSupervision
  );

  const showCriticalSection = Boolean(
    profile.criticalInformation
    || profile.medicalConditions
    || profile.medicationAllergies
    || profile.foodAllergies
    || profile.bloodTypeEnum
    || profile.relevantMedicalInformation
    || medications.length > 0
  );

  const showIdentityExtras = Boolean(
    profile.preferredLanguage
    || profile.dateOfBirth
    || profile.importantInformation
  );

  const careCenterAddress = [
    profile.careCenter?.address,
    profile.careCenter?.postalCode,
    profile.careCenter?.city,
    profile.careCenter?.country,
  ].filter(hasText).join(', ');

  return (
    <div className="carelink-profile-page">
      <div className="carelink-profile-shell">
        <div className="carelink-detect-banner">
          <div className="carelink-detect-icon">
            <i className="fa fa-wifi"></i>
          </div>
          <div>
            <p className="carelink-detect-title">{t('carelink.banner.title')}</p>
            <p className="carelink-detect-subtitle">{t('carelink.banner.success')}</p>
          </div>
        </div>

        <section className="carelink-emergency-card">
          <span className="carelink-emergency-badge">{t('carelink.emergencyBadge')}</span>
          <h1 className="carelink-emergency-name">{displayName}</h1>
          <p className="carelink-emergency-meta">
            {age != null
              ? t('carelink.ageResidence', {
                  age,
                  city: residenceCity || t('carelink.unknownCity'),
                })
              : (residenceCity || profile.importantInformation || '')}
          </p>
          {(
            (profile.identificationNumberPublic && profile.identificationNumber)
            || (profile.healthCardNumberPublic && profile.healthCardNumber)
            || profile.socialSecurityNumber
          ) && (
            <ul className="carelink-emergency-docs">
              {profile.identificationNumberPublic && profile.identificationNumber && (
                <li>
                  <span>{t('carelink.identificationNumber')}</span>
                  <strong>{profile.identificationNumber}</strong>
                </li>
              )}
              {profile.healthCardNumberPublic && profile.healthCardNumber && (
                <li>
                  <span>{t('carelink.healthCardNumber')}</span>
                  <strong>{profile.healthCardNumber}</strong>
                </li>
              )}
              {profile.socialSecurityNumber && (
                <li>
                  <span>{t('carelink.socialSecurityNumber')}</span>
                  <strong>{profile.socialSecurityNumber}</strong>
                </li>
              )}
            </ul>
          )}
        </section>

        {showPrioritySupport && (
          <section className="carelink-priority-banner" aria-label={t('carelink.sections.prioritySupport')}>
            <div className="carelink-priority-header">
              <i className="fa fa-universal-access" aria-hidden="true"></i>
              <h2 className="carelink-priority-title">{t('carelink.sections.prioritySupport')}</h2>
            </div>

            {(profile.hasDisability || hasText(profile.disabilityType)) && (
              <div className="carelink-priority-item">
                <div className="carelink-priority-icon" aria-hidden="true">
                  <i className="fa fa-heartbeat"></i>
                </div>
                <div className="carelink-priority-copy">
                  <span className="carelink-priority-label">{t('carelink.hasDisability')}</span>
                  <strong className="carelink-priority-value">
                    {hasText(profile.disabilityType) ? profile.disabilityType : t('common.yes')}
                  </strong>
                </div>
              </div>
            )}

            {profile.requiresSupervision && (
              <div className="carelink-priority-item is-supervision">
                <div className="carelink-priority-icon" aria-hidden="true">
                  <i className="fa fa-eye"></i>
                </div>
                <div className="carelink-priority-copy">
                  <span className="carelink-priority-label">{t('carelink.requiresSupervision')}</span>
                  <strong className="carelink-priority-value">{t('common.yes')}</strong>
                </div>
              </div>
            )}
          </section>
        )}

        {showIdentityExtras && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.identity')}</h2>
            <div className="carelink-detail-grid">
              <DetailRow label={t('carelink.dateOfBirth')} value={formatDateToDisplay(profile.dateOfBirth)} />
              <DetailRow label={t('carelink.preferredLanguage')} value={profile.preferredLanguage} />
              <DetailRow label={t('carelink.importantInformation')} value={profile.importantInformation} />
            </div>
          </section>
        )}

        {showCriticalSection && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.criticalAlerts')}</h2>
            <div className="carelink-detail-grid">
              {hasText(profile.criticalInformation) && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.criticalInformation')}</span>
                  <div className="carelink-alert-tags">
                    {splitInstructions(profile.criticalInformation).map((item) => (
                      <span className="carelink-alert-tag critical" key={`critical-${item}`}>
                        <i className="fa fa-exclamation-triangle"></i>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hasText(profile.medicalConditions) && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.medicalConditions')}</span>
                  <div className="carelink-alert-tags">
                    {splitInstructions(profile.medicalConditions).map((item) => (
                      <span className="carelink-alert-tag warning" key={`condition-${item}`}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hasText(profile.medicationAllergies) && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.medicationAllergies')}</span>
                  <div className="carelink-alert-tags">
                    {splitInstructions(profile.medicationAllergies).map((item) => (
                      <span className="carelink-alert-tag allergy" key={`med-allergy-${item}`}>
                        <i className="fa fa-medkit"></i>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hasText(profile.foodAllergies) && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.foodAllergies')}</span>
                  <div className="carelink-alert-tags">
                    {splitInstructions(profile.foodAllergies).map((item) => (
                      <span className="carelink-alert-tag allergy" key={`food-allergy-${item}`}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {profile.bloodTypeEnum && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.bloodType')}</span>
                  <div className="carelink-alert-tags">
                    <span className="carelink-alert-tag blood">
                      <i className="fa fa-tint"></i>
                      {t(`carelink.bloodTypes.${profile.bloodTypeEnum}`, {
                        defaultValue: profile.bloodTypeEnum,
                      })}
                    </span>
                  </div>
                </div>
              )}
              {hasText(profile.relevantMedicalInformation) && (
                <div className="carelink-detail-row">
                  <span className="carelink-detail-label">{t('carelink.relevantMedicalInformation')}</span>
                  <div className="carelink-alert-tags">
                    {splitInstructions(profile.relevantMedicalInformation).map((item) => (
                      <span className="carelink-alert-tag info" key={`relevant-${item}`}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {primaryContact && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.primaryContact')}</h2>
            <p className="carelink-contact-name">
              {primaryContact.name}
              {primaryContact.role
                ? ` (${t(`carelink.roles.${primaryContact.role}`, { defaultValue: primaryContact.role })})`
                : ''}
            </p>
            <p className="carelink-contact-hint">{t('carelink.contactAvailable')}</p>
            <div className="carelink-detail-grid" style={{ marginBottom: primaryContact.phone ? '12px' : 0 }}>
              <DetailRow label={t('carelink.phone')} value={primaryContact.phone} />
              <DetailRow label={t('carelink.secondaryPhone')} value={primaryContact.secondaryPhone} />
            </div>
            <CallButton phone={primaryContact.phone} label={t('carelink.callContact')} />
            {hasText(primaryContact.secondaryPhone) && (
              <div style={{ marginTop: '10px' }}>
                <CallButton phone={primaryContact.secondaryPhone} label={t('carelink.callSecondaryPhone')} />
              </div>
            )}
          </section>
        )}

        <InstructionBlock
          title={t('carelink.sections.assistance')}
          text={profile.assistanceInstructions}
        />
        <InstructionBlock
          title={t('carelink.sections.emergency')}
          text={profile.emergencyInstructions}
        />
        <InstructionBlock
          title={t('carelink.sections.supervision')}
          text={profile.supervisionInstructions}
        />

        {(profile.thingsToAvoid || profile.preferences) && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.preferences')}</h2>
            <div className="carelink-detail-grid">
              <DetailRow label={t('carelink.thingsToAvoid')} value={profile.thingsToAvoid} />
              <DetailRow label={t('carelink.preferences')} value={profile.preferences} />
            </div>
          </section>
        )}

        {(profile.communicationInstructions || profile.communicationTypeEnum) && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.communication')}</h2>
            <div className="carelink-detail-grid">
              {profile.communicationTypeEnum && (
                <DetailRow
                  label={t('carelink.communicationType')}
                  value={t(`carelink.communicationTypes.${profile.communicationTypeEnum}`, {
                    defaultValue: profile.communicationTypeEnum,
                  })}
                />
              )}
              <DetailRow label={t('carelink.communicationInstructions')} value={profile.communicationInstructions} />
            </div>
          </section>
        )}

        {medications.length > 0 && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.medications')}</h2>
            {medications.map((medication) => (
              <div className="carelink-medication-item" key={medication.id || medication.name}>
                <p className="carelink-medication-name">
                  {medication.name}
                  {medication.active === false ? ` (${t('carelink.medication.inactive')})` : ''}
                </p>
                <div className="carelink-detail-grid">
                  <DetailRow label={t('carelink.medication.activeIngredient')} value={medication.activeIngredient} />
                  <DetailRow label={t('carelink.medication.dosage')} value={medication.dosage} />
                  <DetailRow label={t('carelink.medication.route')} value={medication.administrationRoute} />
                  <DetailRow label={t('carelink.medication.schedule')} value={medication.schedule} />
                  <DetailRow label={t('carelink.instructions')} value={medication.instructions} />
                </div>
              </div>
            ))}
          </section>
        )}

        {(profile.doctor || profile.careCenter) && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.healthcare')}</h2>
            <div className="carelink-detail-grid">
              {profile.doctor && (
                <>
                  <DetailRow label={t('carelink.doctor')} value={profile.doctor.name} />
                  <DetailRow label={t('carelink.specialty')} value={profile.doctor.specialty} />
                  <DetailRow label={t('carelink.medicalCenter')} value={profile.doctor.medicalCenter} />
                  <DetailRow label={t('carelink.phone')} value={profile.doctor.phone} />
                  <CallButton phone={profile.doctor.phone} label={t('carelink.callDoctor')} />
                </>
              )}
              {profile.careCenter && (
                <>
                  <DetailRow label={t('carelink.careCenter')} value={profile.careCenter.name} />
                  {profile.careCenter.type && (
                    <DetailRow
                      label={t('carelink.careCenterType')}
                      value={t(`carelink.careCenterTypes.${profile.careCenter.type}`, {
                        defaultValue: profile.careCenter.type,
                      })}
                    />
                  )}
                  <DetailRow label={t('carelink.address')} value={careCenterAddress} />
                  <DetailRow label={t('carelink.phone')} value={profile.careCenter.phone} />
                  <CallButton phone={profile.careCenter.phone} label={t('carelink.callCareCenter')} />
                </>
              )}
            </div>
          </section>
        )}

        {otherContacts.length > 0 && (
          <section className="carelink-section-card">
            <h2 className="carelink-section-title">{t('carelink.sections.otherContacts')}</h2>
            <div className="carelink-other-contacts">
              {otherContacts.map((contact) => (
                <div
                  className="carelink-other-contact-item"
                  key={contact.id || `${contact.name}-${contact.phone}`}
                >
                  <p className="carelink-contact-name">
                    {contact.name}
                    {contact.role
                      ? ` (${t(`carelink.roles.${contact.role}`, { defaultValue: contact.role })})`
                      : ''}
                  </p>
                  <div className="carelink-detail-grid" style={{ marginBottom: '10px' }}>
                    <DetailRow label={t('carelink.phone')} value={contact.phone} />
                    <DetailRow label={t('carelink.secondaryPhone')} value={contact.secondaryPhone} />
                  </div>
                  <CallButton phone={contact.phone} label={t('carelink.callContact')} />
                  {hasText(contact.secondaryPhone) && (
                    <div style={{ marginTop: '10px' }}>
                      <CallButton phone={contact.secondaryPhone} label={t('carelink.callSecondaryPhone')} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default CareLink;
